import { useDebounce } from '@/hooks/useDebounce';
import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import DropdownWithRadio from './RadioButtons';

export type ClientFilterField =
  | {
      type: 'search';
      key: string;
      placeholder?: string;
    }
  | {
      type: 'select';
      key: string;
      label?: string;
      options: { label: string; value: string }[];
      defaultValue?: string;
    }
  | {
      type: 'radio-select';
      key: string;
      label: string;
      options: string[];
      defaultValue?: string | null;
    }
  | {
      type: 'date';
      key: string;
      label?: string;
    }
  | {
      type: 'number';
      key: string;
      label?: string;
      min?: number;
      max?: number;
    };

interface ClientSearchFilterProps<T> {
  fields: ClientFilterField[];
  data: T[];
  onFilter: (filtered: T[]) => void;
  searchableKeys?: (keyof T)[];
  customFilterFn?: (item: T, filters: Record<string, any>) => boolean;
  className?: string;
}

const ClientSearchFilter = <T extends Record<string, any>>({
  fields,
  data,
  onFilter,
  searchableKeys = [],
  customFilterFn,
  className = '',
}: ClientSearchFilterProps<T>) => {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const initialState = fields.reduce(
    (acc, field) => {
      acc[field.key] = 'defaultValue' in field && field.defaultValue ? field.defaultValue : '';
      return acc;
    },
    {} as Record<string, any>,
  );

  const [filters, setFilters] = useState(initialState);
  const debouncedFilters = useDebounce(filters, 300);

  useEffect(() => {
    let filtered = [...data];

    if (customFilterFn) {
      filtered = filtered.filter((item) => customFilterFn(item, debouncedFilters));
    } else {
      fields.forEach((f) => {
        const val = debouncedFilters[f.key];

        if (f.type === 'search') {
          if (val.trim() !== '' && searchableKeys.length > 0) {
            const s = val.toLowerCase();
            filtered = filtered.filter((item) =>
              searchableKeys.some((key) => String(item[key]).toLowerCase().includes(s)),
            );
          }
        }

        if (f.type === 'select') {
          if (val !== '' && val !== f.options[0]?.value) {
            filtered = filtered.filter((item) => String(item[f.key]) === val);
          }
        }

        if (f.type === 'date') {
          if (val !== '') {
            filtered = filtered.filter((item) => {
              const dateValue = String(item[f.key]).slice(0, 10);
              return dateValue === val;
            });
          }
        }

        if (f.type === 'number') {
          if (val !== '') {
            filtered = filtered.filter((item) => Number(item[f.key]) === Number(val));
          }
        }
      });
    }

    onFilter(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilters, data]);
  // Removed: fields, searchableKeys, customFilterFn, onFilter

  const updateField = (key: string, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div
      className={`flex justify-between items-stretch gap-3 flex-wrap bg-[#F2EBEE] p-2 rounded-lg ${className}`}
    >
      {fields.map((field) => {
        if (field.type === 'search') {
          return (
            <div
              key={field.key}
              className="flex flex-1 items-center bg-[#F2EBEE] text-[#4A082D] border border-[#E1D4DB] rounded-lg px-3"
            >
              <Search className="text-[#4A082D] h-5 w-5" />
              <input
                type="text"
                placeholder={field.placeholder || 'Search...'}
                className="px-2 w-full py-3 bg-transparent focus:outline-none text-sm placeholder:text-[#5c1f42]"
                value={filters[field.key]}
                onChange={(e) => updateField(field.key, e.target.value)}
              />
            </div>
          );
        }

        if (field.type === 'radio-select') {
          const getAllOption = (label: string) => {
            const lower = label.toLowerCase();
            if (lower.includes('status')) return 'All Status';
            if (lower.includes('language')) return 'All Languages';
            if (lower.includes('role')) return 'All Role';
            if (lower.includes('type')) return 'All Type';
            if (lower.includes('filter')) return 'Filter By';
            if (lower.includes('select')) return 'Select All';
            return `All ${label}`;
          };

          const allOption = getAllOption(field.label);

          return (
            <DropdownWithRadio
              key={field.key}
              label={field.label}
              options={[allOption, ...field.options]}
              selected={filters[field.key]}
              onChange={(value) => updateField(field.key, value)}
              isOpen={openDropdown === field.key}
              onOpen={() => setOpenDropdown(field.key)}
              onClose={() => setOpenDropdown((prev) => (prev === field.key ? null : prev))}
              placeholder={allOption}
            />
          );
        }

        if (field.type === 'select') {
          return (
            <select
              key={field.key}
              value={filters[field.key]}
              onChange={(e) => updateField(field.key, e.target.value)}
              className="px-2 py-3 border bg-[#F2EBEE] border-[#4A082D1A] rounded-md text-sm text-[#4A082D]"
            >
              {field.options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            // </div>
          );
        }

        if (field.type === 'date') {
          return (
            // <div key={field.key} className="flex flex-col">
            //   {field.label && (
            //     <label className="text-xs mb-1 text-[#4A082D]/80">{field.label}</label>
            //   )}
            <input
              type="date"
              value={filters[field.key]}
              onChange={(e) => updateField(field.key, e.target.value)}
              className="px-3 py-2 border bg-[#F2EBEE] border-[#4A082D1A] rounded-md text-sm text-[#4A082D]"
            />
            // </div>
          );
        }

        if (field.type === 'number') {
          return (
            <div key={field.key} className="flex flex-col">
              {field.label && (
                <label className="text-xs mb-1 text-[#4A082D]/80">{field.label}</label>
              )}
              <input
                type="number"
                value={filters[field.key]}
                onChange={(e) => updateField(field.key, e.target.value)}
                min={field.min}
                max={field.max}
                className="px-3 py-2 border bg-[#F2EBEE] border-[#4A082D1A] rounded-md text-sm focus:outline-none w-24"
              />
            </div>
          );
        }

        return null;
      })}

      {/* {Object.values(filters).some(
        (val) => val !== initialState[Object.keys(filters).find((k) => filters[k] === val) || ''],
      ) && (
        <button
          onClick={resetFilters}
          className="px-3 py-2 text-sm bg-[#621C43] text-white rounded-md hover:bg-[#4A082D] transition-colors"
        >
          Reset
        </button>
      )} */}
    </div>
  );
};

export default ClientSearchFilter;
